import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CalendarBlank, Warning } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  PAYMENT_OPTIONS, SOURCE_OPTIONS, toneClasses, validateInvoiceForm,
  formFromLifecycle, relativeDays,
} from "@/lib/renewalLifecycle";

// ─── Upcoming Renewals (platform admin dashboard) ───────────────────
//
// Track 6 ops cleanup (2026-09-23). The widget used to show one column,
// "Draft invoice posts", with "(overdue)" appended whenever renewal − 42
// days had passed — even for invoices already issued in Xero. Every
// renewal in the window read as overdue on the day this was rewritten.
//
// Each row now shows four separate facts, all derived server-side by
// `backend/renewal_lifecycle.py`:
//   renewal date · invoice target (or issued date) · payment · status
// "Invoice overdue" means OUR invoice has not been issued and its
// target date has passed. It is never shown once an invoice exists,
// and it never means the customer is late paying.
// ─────────────────────────────────────────────────────────────────────

const STAGE_SUMMARY = [
  { stages: ["needs_invoicing"], label: "Needs invoicing", tone: "neutral" },
  { stages: ["invoice_overdue"], label: "Invoice overdue", tone: "danger" },
  { stages: ["awaiting_payment", "part_paid", "disputed"], label: "Invoiced — awaiting payment", tone: "warning" },
  { stages: ["paid", "written_off"], label: "Paid / closed", tone: "success" },
];

const inputCss =
  "w-full border border-[#E5E5E5] px-2 py-1.5 text-sm focus:outline-none focus:border-[#0055FF]";
const labelCss =
  "block text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1";

function RenewalInvoiceDialog({ item, asOf, onClose, onSaved }) {
  const lc = item.lifecycle || {};
  const [form, setForm] = useState(() => formFromLifecycle(lc));
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const showPaidOn = form.payment_status === "paid" || form.payment_status === "part_paid";
  // The paid date is hidden (and not sent) for statuses that have not been
  // paid, so it must not be validated either — otherwise moving a row off
  // Paid would leave Save disabled by a field the operator cannot see.
  const submitted = { ...form, paid_on: showPaidOn ? form.paid_on : "" };
  const clientError = validateInvoiceForm(submitted, asOf);

  const save = async () => {
    if (clientError) return;
    setSaving(true);
    setServerError(null);
    try {
      await api.put(`/dashboard/renewals/${item.id}/invoice`, submitted);
      toast.success(`${item.name}: renewal invoice saved.`);
      onSaved();
    } catch (e) {
      setServerError(e.response?.data?.detail || "Could not save. Nothing was changed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirmClear) { setConfirmClear(true); return; }
    setSaving(true);
    setServerError(null);
    try {
      await api.post(`/dashboard/renewals/${item.id}/status`, {});
      toast.success(`${item.name}: marked not invoiced.`);
      onSaved();
    } catch (e) {
      setServerError(e.response?.data?.detail || "Could not clear. Nothing was changed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid={`renewal-invoice-dialog-${item.id}`}>
        <DialogHeader>
          <DialogTitle className="text-base">{item.name} — renewal {lc.renewal_date}</DialogTitle>
          <DialogDescription className="text-xs">
            Record this cycle's renewal invoice and payment. Pulse does not sync with
            Xero or FinOps — enter what those systems show. Invoice target was{" "}
            <strong>{lc.invoice_target_date}</strong> (renewal − 6 weeks).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-1">
          <div>
            <label className={labelCss} htmlFor="ri-issued">Invoice issued</label>
            <input id="ri-issued" type="date" max={asOf} className={inputCss}
                   value={form.invoice_issued_on} onChange={set("invoice_issued_on")} />
            <p className="text-[10px] text-[#A3A3A3] mt-0.5">Leave blank if unknown.</p>
          </div>
          <div>
            <label className={labelCss} htmlFor="ri-number">Invoice number</label>
            <input id="ri-number" type="text" maxLength={80} className={inputCss}
                   value={form.invoice_number} onChange={set("invoice_number")} placeholder="e.g. INV-1234" />
          </div>
          <div>
            <label className={labelCss} htmlFor="ri-source">Issued in</label>
            <select id="ri-source" className={inputCss} value={form.invoice_source} onChange={set("invoice_source")}>
              <option value="">Not stated</option>
              {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCss} htmlFor="ri-payment">Payment</label>
            <select id="ri-payment" className={inputCss} value={form.payment_status} onChange={set("payment_status")}
                    data-testid="renewal-payment-select">
              {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {showPaidOn && (
            <div>
              <label className={labelCss} htmlFor="ri-paid">
                Paid date{form.payment_status === "paid" ? " *" : ""}
              </label>
              <input id="ri-paid" type="date" max={asOf} className={inputCss}
                     value={form.paid_on} onChange={set("paid_on")} />
            </div>
          )}
          <div className="col-span-2">
            <label className={labelCss} htmlFor="ri-note">Note (optional)</label>
            <textarea id="ri-note" rows={2} maxLength={500} className={inputCss}
                      value={form.payment_note} onChange={set("payment_note")} />
          </div>
        </div>

        {(clientError || serverError) && (
          <p className="text-xs text-[#B91C1C] mt-1" role="alert" data-testid="renewal-invoice-error">
            {serverError || clientError}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 mt-2">
          {lc.invoiced ? (
            <button type="button" onClick={clear} disabled={saving}
                    className="text-[11px] text-[#525252] hover:text-[#B91C1C] underline disabled:opacity-50">
              {confirmClear ? "Click again to confirm — clears this cycle's invoice record" : "Mark not invoiced"}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving}
                    className="px-3 py-1.5 text-xs border border-[#E5E5E5] text-[#525252] hover:bg-[#FAFAFA] disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving || !!clientError}
                    data-testid="renewal-invoice-save"
                    className="px-3 py-1.5 text-xs font-bold bg-[#0055FF] text-white hover:bg-[#003ECB] disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenewalRow({ it, navigate, onEdit, canEdit }) {
  const lc = it.lifecycle || {};
  const issuedLine = lc.invoiced
    ? (lc.invoice_issued_on ? `Issued ${lc.invoice_issued_on}` : "Issued · date not recorded")
    : `Target ${lc.invoice_target_date}`;
  const refLine = [lc.invoice_number ? `#${lc.invoice_number}` : null, lc.invoice_source_label]
    .filter(Boolean).join(" · ");
  const payment = !lc.invoiced
    ? "—"
    : (lc.payment_status === "paid" && lc.paid_on ? `Paid ${lc.paid_on}` : lc.payment_label);

  return (
    <tr className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA] align-top" data-testid={`renewal-row-${it.id}`}>
      <td className="px-4 py-2.5">
        <button type="button" className="font-semibold text-[#0A0A0A] hover:text-[#0055FF] text-left"
                onClick={() => navigate(`/clients/${it.id}`)}>
          {it.name}
        </button>
        {it.estimated_amount != null && (
          <div className="text-[10px] text-[#A3A3A3] tabular-nums">Est. ${it.estimated_amount.toLocaleString()}</div>
        )}
      </td>
      <td className="px-3 py-2.5 tabular-nums">
        <div className="text-[#0A0A0A]">{lc.renewal_date}</div>
        <div className="text-[10px] text-[#A3A3A3]">{relativeDays(lc.days_to_renewal)}</div>
      </td>
      <td className="px-3 py-2.5 tabular-nums" data-testid={`renewal-invoice-cell-${it.id}`}>
        <div className={lc.invoiced && !lc.invoice_issued_on ? "text-[#525252] italic" : "text-[#0A0A0A]"}>
          {issuedLine}
        </div>
        {refLine && <div className="text-[10px] text-[#A3A3A3]">{refLine}</div>}
        {!lc.invoiced && it.finops_draft_invoice_number && (
          <div className="text-[10px] text-[#A3A3A3]" title="A Pulse-generated FinOps draft exists for this cycle. A draft is not an issued invoice.">
            FinOps draft {it.finops_draft_invoice_number}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-[#0A0A0A]" title={lc.payment_note || undefined}
          data-testid={`renewal-payment-cell-${it.id}`}>
        {payment}
        {lc.payment_note && <div className="text-[10px] text-[#A3A3A3] truncate max-w-[160px]">{lc.payment_note}</div>}
      </td>
      <td className="px-3 py-2.5">
        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${toneClasses(lc.tone)}`}
              data-testid={`renewal-stage-${it.id}`} data-stage={lc.stage}>
          {lc.stage_heading}
        </span>
        {!lc.invoiced && <div className="text-[11px] text-[#525252] mt-0.5">{lc.label}</div>}
      </td>
      <td className="px-3 py-2.5 text-right">
        {canEdit && (
          <button type="button" onClick={() => onEdit(it)} data-testid={`renewal-edit-${it.id}`}
                  className="px-2 py-1 text-[11px] border border-[#E5E5E5] text-[#525252] hover:border-[#0055FF] hover:text-[#0055FF] whitespace-nowrap">
            {lc.invoiced ? "Update" : "Record invoice"}
          </button>
        )}
      </td>
    </tr>
  );
}

export default function RenewalsWidget({ renewals, navigate, onReload }) {
  const [editing, setEditing] = useState(null);

  if (renewals?.error) {
    return (
      <div className="bg-white border border-[#E5E5E5] px-4 py-6 text-center" data-testid="renewals-widget-error">
        <Warning size={22} className="mx-auto text-[#D97706] mb-2" />
        <p className="text-sm text-[#0A0A0A]">Could not load upcoming renewals.</p>
        <p className="text-[11px] text-[#A3A3A3] mt-1">
          Refresh the page. If this continues, contact support{renewals.status ? ` (HTTP ${renewals.status})` : ""}.
        </p>
      </div>
    );
  }

  const items = renewals.items || [];
  const counts = renewals.stage_counts || {};
  const canEdit = renewals.schema_complete !== false;

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="renewals-widget">
      <div className="flex items-start justify-between px-4 py-3 border-b border-[#E5E5E5] flex-wrap gap-2">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Upcoming Renewals — next {renewals.window_days} days</h3>
          <p className="text-[11px] text-[#A3A3A3] mt-0.5">
            {renewals.total_count} client{renewals.total_count === 1 ? "" : "s"}
            {renewals.total_annual_fees > 0 && (
              <span> · <strong className="text-[#0A0A0A]">${renewals.total_annual_fees.toLocaleString()}</strong> in fees</span>
            )}
            {" · "}Invoice target = renewal date − 6 weeks. "Invoice overdue" means not yet invoiced, not late payment.
          </p>
          {items.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="renewals-stage-summary">
              {STAGE_SUMMARY.map((g) => {
                const n = g.stages.reduce((a, s) => a + (counts[s] || 0), 0);
                return (
                  <span key={g.label}
                        className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${n ? toneClasses(g.tone) : toneClasses("muted") + " opacity-60"}`}>
                    {g.label}: {n}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <button onClick={() => navigate("/clients")} className="text-[10px] font-bold text-[#0055FF] hover:text-[#003ECB] uppercase tracking-wider inline-flex items-center gap-1">
          All Clients <ArrowRight size={10} weight="bold" />
        </button>
      </div>
      {!canEdit && (
        <p className="px-4 py-2 text-[11px] text-[#9A3412] bg-[#FFF7ED] border-b border-[#FED7AA]">
          Invoice and payment tracking is unavailable until the backend's database migration has run. Renewal dates and invoice targets are still shown.
        </p>
      )}
      {items.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <CalendarBlank size={28} className="mx-auto text-[#A3A3A3] mb-2" />
          <p className="text-sm text-[#525252]">No client renewals due in the next {renewals.window_days} days.</p>
          <p className="text-[11px] text-[#A3A3A3] mt-1">When one comes up you'll see its renewal date, invoice target date and payment status here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Client</th>
                <th className="text-left px-3 py-2 font-bold">Renewal</th>
                <th className="text-left px-3 py-2 font-bold" title="Before invoicing: the date the invoice should be issued (renewal − 6 weeks). After: the date it was issued.">Invoice target / issued</th>
                <th className="text-left px-3 py-2 font-bold">Payment</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 25).map((it) => (
                <RenewalRow key={it.id} it={it} navigate={navigate} onEdit={setEditing} canEdit={canEdit} />
              ))}
              {items.length > 25 && (
                <tr className="border-t border-[#F5F5F5]">
                  <td colSpan={6} className="px-4 py-2 text-[10px] text-[#A3A3A3] text-center">
                    +{items.length - 25} more renewals in window — see Clients page
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <RenewalInvoiceDialog
          item={editing}
          asOf={renewals.as_of}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}
